/**
 * Validation utility functions for form fields and data validation
 */

/**
 * Validates Turkish ID Number (TC Kimlik Numarası)
 * @param {string|number} tcNumber - The TC number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateTCNumber = (tcNumber) => {
    if (!tcNumber) return true; // Boş değer geçerli (opsiyonel alan)

    const tcStr = tcNumber.toString();

    // 11 hane kontrolü
    if (tcStr.length !== 11) return false;

    // Sadece rakam kontrolü
    if (!/^\d{11}$/.test(tcStr)) return false;

    // İlk hane 0 olamaz
    if (tcStr[0] === '0') return false;

    // TC Kimlik numarası algoritması
    const digits = tcStr.split('').map(Number);

    // 1, 3, 5, 7, 9. hanelerin toplamı
    const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];

    // 2, 4, 6, 8. hanelerin toplamı
    const evenSum = digits[1] + digits[3] + digits[5] + digits[7];

    // 10. hane kontrolü: (oddSum * 7 - evenSum) % 10
    const checkDigit10 = (oddSum * 7 - evenSum) % 10;
    if (checkDigit10 !== digits[9]) return false;

    // 11. hane kontrolü: (oddSum + evenSum + digits[9]) % 10
    const checkDigit11 = (oddSum + evenSum + digits[9]) % 10;
    if (checkDigit11 !== digits[10]) return false;

    return true;
};

/**
 * Validates email format
 * @param {string} email - The email to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateEmail = (email) => {
    if (!email) return true; // Boş değer geçerli (opsiyonel alan)

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validates phone number format (Turkish format)
 * @param {string|number} phone - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Boş değer geçerli (opsiyonel alan)

    const phoneStr = phone.toString().replace(/\s/g, ''); // Boşlukları kaldır
    const phoneRegex = /^(\+90|0)?[5][0-9]{9}$/; // Türk telefon numarası formatı
    return phoneRegex.test(phoneStr);
};

/**
 * Validates postal code format
 * @param {string|number} postalCode - The postal code to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePostalCode = (postalCode) => {
    if (!postalCode) return true; // Boş değer geçerli (opsiyonel alan)

    const postalStr = postalCode.toString();
    const postalRegex = /^\d{5}$/; // 5 haneli posta kodu
    return postalRegex.test(postalStr);
};

/**
 * Validates required field
 * @param {any} value - The value to validate
 * @returns {boolean} - True if valid (not empty), false otherwise
 */
export const validateRequired = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
};

/**
 * Validates minimum length
 * @param {string} value - The string to validate
 * @param {number} minLength - Minimum required length
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateMinLength = (value, minLength) => {
    if (!value) return true; // Boş değer geçerli (opsiyonel alan)
    return value.toString().length >= minLength;
};

/**
 * Validates maximum length
 * @param {string} value - The string to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateMaxLength = (value, maxLength) => {
    if (!value) return true; // Boş değer geçerli (opsiyonel alan)
    return value.toString().length <= maxLength;
};

/**
 * Validates numeric value
 * @param {any} value - The value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateNumeric = (value) => {
    if (!value) return true; // Boş değer geçerli (opsiyonel alan)
    return !isNaN(value) && !isNaN(parseFloat(value));
};

/**
 * Validates positive number
 * @param {any} value - The value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePositiveNumber = (value) => {
    if (!value) return true; // Boş değer geçerli (opsiyonel alan)
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
};
